import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react/react/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import data from '../../src/Autocomplete/Autocomplete.docs.json';
import { Autocomplete } from '@primer/react';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentProps = makeShortcode("ComponentProps");
const ComponentChecklist = makeShortcode("ComponentChecklist");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`The `}<inlineCode parentName="p">{`Autocomplete`}</inlineCode>{` component is comprised of an `}<inlineCode parentName="p">{`Autocomplete.Input`}</inlineCode>{` component that a user types into, and a `}<inlineCode parentName="p">{`Autocomplete.Menu`}</inlineCode>{` component that displays the list of selectable values.`}</p>
    <h2>{`Anatomy`}</h2>
    <h3>{`Autocomplete.Input`}</h3>
    <p>{`The text input is used to filter the options in the dropdown menu. It is also used to show the selected value (or values).`}</p>
    <p>{`The default input rendered is the `}<inlineCode parentName="p">{`TextInput`}</inlineCode>{` component. A different text input component may be rendered by passing a different component to the `}<inlineCode parentName="p">{`as`}</inlineCode>{` prop.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`Autocomplete.Input`}</inlineCode>{` should not be rendered without a `}<inlineCode parentName="p">{`<label>`}</inlineCode>{` who's `}<inlineCode parentName="p">{`htmlFor`}</inlineCode>{` prop matches the `}<inlineCode parentName="p">{`Autocomplete.Input`}</inlineCode>{`'s `}<inlineCode parentName="p">{`id`}</inlineCode>{` prop`}</p>
    <h3>{`Autocomplete.Overlay`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`Autocomplete.Overlay`}</inlineCode>{` wraps the `}<inlineCode parentName="p">{`Autocomplete.Menu`}</inlineCode>{` to display it in an `}<a parentName="p" {...{
        "href": "./Overlay"
      }}>{`Overlay`}</a>{` component.
This component takes the same props as the `}<inlineCode parentName="p">{`Overlay`}</inlineCode>{` component.
Most `}<inlineCode parentName="p">{`Autocomplete`}</inlineCode>{` implementations will use the `}<inlineCode parentName="p">{`Autocomplete.Overlay`}</inlineCode>{` component, but there could be special cases where the `}<inlineCode parentName="p">{`Autocomplete.Menu`}</inlineCode>{` should be rendered directly after the `}<inlineCode parentName="p">{`Autocomplete.Input`}</inlineCode>{` (for example: an `}<inlineCode parentName="p">{`Autocomplete`}</inlineCode>{` that is already being rendered in an `}<inlineCode parentName="p">{`Overlay`}</inlineCode>{`).`}</p>
    <h3>{`Autocomplete.Menu`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`Autocomplete.Menu`}</inlineCode>{` component renders a list of selectable options in a non-modal dialog. The list is filtered and sorted to make it as easy as possible to find the option/s that a user is looking for.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`Autocomplete.Menu`}</inlineCode>{` component should be passed an `}<inlineCode parentName="p">{`aria-labelledby`}</inlineCode>{` prop that matches the `}<inlineCode parentName="p">{`id`}</inlineCode>{` prop of the `}<inlineCode parentName="p">{`<label>`}</inlineCode>{` associated with the `}<inlineCode parentName="p">{`Autocomplete.Input`}</inlineCode></p>
    <h4>{`Customizing how menu items are rendered`}</h4>
    <p>{`By default, menu items are just rendered as a single line of text. The list in the menu is rendered using the `}<a parentName="p" {...{
        "href": "/ActionList"
      }}>{`Action List`}</a>{` component, so menu items can be rendered with all of the same options as Action List items.
However, the `}<inlineCode parentName="p">{`renderGroup`}</inlineCode>{`, `}<inlineCode parentName="p">{`groupMetadata`}</inlineCode>{`, and `}<inlineCode parentName="p">{`renderItem`}</inlineCode>{` props have not been implemented yet.`}</p>
    <h4>{`Sorting menu items`}</h4>
    <p>{`Items can be displayed in any order that makes sense, but the `}<inlineCode parentName="p">{`Autocomplete.Menu`}</inlineCode>{` component comes with a default sort behavior to make it easy to find selected items. The default behavior is to sort selected items to the top of the list after the menu has been closed.`}</p>
    <p>{`A function may be passed to the `}<inlineCode parentName="p">{`sortOnCloseFn`}</inlineCode>{` prop if this default sorting logic is not helpful for your use case. The sort function will be only be called after the menu is closed so that items don't shift while users are trying to make a selection.`}</p>
    <h4>{`Filtering menu items`}</h4>
    <p>{`By default, menu items are filtered based on whether or not they match the value of the text input. The default filter is case-insensitive.`}</p>
    <p>{`A function may be passed to the `}<inlineCode parentName="p">{`filterFn`}</inlineCode>{` prop if this default filtering behavior does not make sense for your use case.`}</p>
    <h2>{`Examples`}</h2>
    <h3>{`Basic example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<FormControl>
  <FormControl.Label id="autocompleteLabel-basic">Pick a branch</FormControl.Label>
  <Autocomplete>
    <Autocomplete.Input />
    <Autocomplete.Overlay>
      <Autocomplete.Menu
        items={[
          {text: 'main', id: 0},
          {text: 'autocomplete-tests', id: 1},
          {text: 'a11y-improvements', id: 2},
          {text: 'button-bug-fixes', id: 3},
          {text: 'radio-input-component', id: 4},
          {text: 'release-1.0.0', id: 5},
          {text: 'text-input-implementation', id: 6},
          {text: 'visual-design-tweaks', id: 7},
        ]}
        selectedItemIds={[]}
        aria-labelledby="autocompleteLabel-basic"
      />
    </Autocomplete.Overlay>
  </Autocomplete>
</FormControl>
`}</code></pre>
    <h3>{`Autocomplete.Input with a custom text input`}</h3>
    <p>{`In this example, we're passing a `}<a parentName="p" {...{
        "href": "/TextInputWithTokens"
      }}>{`TextInputWithTokens`}</a>{` component`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const CustomTextInputExample = () => {
  const [tokens, setTokens] = React.useState([{text: 'zero', id: 0}])
  const selectedTokenIds = tokens.map(token => token.id)
  const [selectedItemIds, setSelectedItemIds] = React.useState(selectedTokenIds)
  const onTokenRemove = tokenId => {
    setTokens(tokens.filter(token => token.id !== tokenId))
    setSelectedItemIds(selectedItemIds.filter(id => id !== tokenId))
  }
  const onSelectedChange = newlySelectedItems => {
    if (!Array.isArray(newlySelectedItems)) {
      return
    }

    setSelectedItemIds(newlySelectedItems.map(item => item.id))

    if (newlySelectedItems.length < selectedItemIds.length) {
      const newlySelectedItemIds = newlySelectedItems.map(({id}) => id)
      const removedItemIds = selectedTokenIds.filter(id => !newlySelectedItemIds.includes(id))

      for (const removedItemId of removedItemIds) {
        onTokenRemove(removedItemId)
      }

      return
    }

    setTokens(newlySelectedItems.map(({id, text}) => ({id, text})))
  }

  return (
    <FormControl>
      <FormControl.Label id="autocompleteLabel-customInput">Pick options</FormControl.Label>
      <Autocomplete>
        <Autocomplete.Input as={TextInputWithTokens} tokens={tokens} onTokenRemove={onTokenRemove} />
        <Autocomplete.Overlay>
          <Autocomplete.Menu
            items={[
              {text: 'zero', id: 0},
              {text: 'one', id: 1},
              {text: 'two', id: 2},
              {text: 'three', id: 3},
              {text: 'four', id: 4},
              {text: 'five', id: 5},
              {text: 'six', id: 6},
              {text: 'seven', id: 7},
            ]}
            selectedItemIds={selectedItemIds}
            onSelectedChange={onSelectedChange}
            selectionVariant="multiple"
            aria-labelledby="autocompleteLabel-customInput"
          />
        </Autocomplete.Overlay>
      </Autocomplete>
    </FormControl>
  )
}

render(<CustomTextInputExample />)
`}</code></pre>
    <h3>{`Without `}<inlineCode parentName="h3">{`Autocomplete.Overlay`}</inlineCode></h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<FormControl>
  <FormControl.Label id="autocompleteLabel-withoutOverlay">Pick a branch</FormControl.Label>
  <Autocomplete>
    <Autocomplete.Input />
    <Autocomplete.Menu
      items={[
        {text: 'main', id: 0},
        {text: 'autocomplete-tests', id: 1},
        {text: 'a11y-improvements', id: 2},
        {text: 'button-bug-fixes', id: 3},
        {text: 'radio-input-component', id: 4},
        {text: 'release-1.0.0', id: 5},
        {text: 'text-input-implementation', id: 6},
        {text: 'visual-design-tweaks', id: 7},
      ]}
      selectedItemIds={[]}
      aria-labelledby="autocompleteLabel-withoutOverlay"
    />
  </Autocomplete>
</FormControl>
`}</code></pre>
    <h4>{`Render items using `}<inlineCode parentName="h4">{`ActionList.Item`}</inlineCode>{` props`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`function getColorCircle(color) {
  return function () {
    return (
      <Box
        bg={color}
        borderColor={color}
        width={14}
        height={14}
        borderRadius={10}
        margin="auto"
        borderWidth="1px"
        borderStyle="solid"
      />
    )
  }
}

const CustomRenderedItemExample = () => {
  const [tokens, setTokens] = React.useState([
    {text: 'enhancement', id: 1, fillColor: '#a2eeef'},
    {text: 'bug', id: 2, fillColor: '#d73a4a'},
    {text: 'good first issue', id: 3, fillColor: '#0cf478'},
  ])
  const selectedTokenIds = tokens.map(token => token.id)
  const [selectedItemIds, setSelectedItemIds] = React.useState(selectedTokenIds)
  const onTokenRemove = tokenId => {
    setTokens(tokens.filter(token => token.id !== tokenId))
    setSelectedItemIds(selectedItemIds.filter(id => id !== tokenId))
  }
  const onSelectedChange = newlySelectedItems => {
    if (!Array.isArray(newlySelectedItems)) {
      return
    }

    setSelectedItemIds(newlySelectedItems.map(item => item.id))

    if (newlySelectedItems.length < selectedItemIds.length) {
      const newlySelectedItemIds = newlySelectedItems.map(({id}) => id)
      const removedItemIds = selectedTokenIds.filter(id => !newlySelectedItemIds.includes(id))

      for (const removedItemId of removedItemIds) {
        onTokenRemove(removedItemId)
      }

      return
    }

    setTokens(newlySelectedItems.map(({id, text, metadata}) => ({id, text, fillColor: metadata.fillColor})))
  }

  return (
    <FormControl>
      <FormControl.Label id="autocompleteLabel-customRenderedItem">Pick labels</FormControl.Label>
      <Autocomplete>
        <Autocomplete.Input
          as={TextInputWithTokens}
          tokens={tokens}
          tokenComponent={IssueLabelToken}
          onTokenRemove={onTokenRemove}
        />
        <Autocomplete.Overlay>
          <Autocomplete.Menu
            items={[
              {leadingVisual: getColorCircle('#a2eeef'), text: 'enhancement', id: 1, metadata: {fillColor: '#a2eeef'}},
              {leadingVisual: getColorCircle('#d73a4a'), text: 'bug', id: 2, metadata: {fillColor: '#d73a4a'}},
              {
                leadingVisual: getColorCircle('#0cf478'),
                text: 'good first issue',
                id: 3,
                metadata: {fillColor: '#0cf478'},
              },
              {leadingVisual: getColorCircle('#ffd78e'), text: 'design', id: 4, metadata: {fillColor: '#ffd78e'}},
              {leadingVisual: getColorCircle('#ff0000'), text: 'blocker', id: 5, metadata: {fillColor: '#ff0000'}},
              {leadingVisual: getColorCircle('#a4f287'), text: 'backend', id: 6, metadata: {fillColor: '#a4f287'}},
              {leadingVisual: getColorCircle('#8dc6fc'), text: 'frontend', id: 7, metadata: {fillColor: '#8dc6fc'}},
            ]}
            selectedItemIds={selectedItemIds}
            onSelectedChange={onSelectedChange}
            selectionVariant="multiple"
            aria-labelledby="autocompleteLabel-customRenderedItem"
          />
        </Autocomplete.Overlay>
      </Autocomplete>
    </FormControl>
  )
}

render(<CustomRenderedItemExample />)
`}</code></pre>
    <h4>{`Customize sort when the menu is re-opened`}</h4>
    <p>{`In this example, selected items get sorted to the end. By default, they are sorted to the beginning.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const CustomSortAfterMenuClose = () => {
  const [selectedItemIds, setSelectedItemIds] = React.useState([])
  const isItemSelected = itemId => selectedItemIds.includes(itemId)
  const onSelectedChange = newlySelectedItems => {
    if (!Array.isArray(newlySelectedItems)) {
      return
    }

    setSelectedItemIds(newlySelectedItems.map(item => item.id))
  }
  const customSortFn = (itemIdA, itemIdB) =>
    isItemSelected(itemIdA) === isItemSelected(itemIdB) ? 0 : isItemSelected(itemIdA) ? 1 : -1

  return (
    <FormControl>
      <FormControl.Label id="autocompleteLabel-sortAfterClose">Pick branches</FormControl.Label>
      <Autocomplete>
        <Autocomplete.Input />
        <Autocomplete.Overlay>
          <Autocomplete.Menu
            items={[
              {text: 'main', id: 0},
              {text: 'autocomplete-tests', id: 1},
              {text: 'a11y-improvements', id: 2},
              {text: 'button-bug-fixes', id: 3},
              {text: 'radio-input-component', id: 4},
              {text: 'release-1.0.0', id: 5},
              {text: 'text-input-implementation', id: 6},
              {text: 'visual-design-tweaks', id: 7},
            ]}
            selectedItemIds={selectedItemIds}
            aria-labelledby="autocompleteLabel-sortAfterClose"
            onSelectedChange={onSelectedChange}
            sortOnCloseFn={customSortFn}
            selectionVariant="multiple"
          />
        </Autocomplete.Overlay>
      </Autocomplete>
    </FormControl>
  )
}

render(<CustomSortAfterMenuClose />)
`}</code></pre>
    <h4>{`Custom filtering`}</h4>
    <p>{`In this example, we show any items whose `}<inlineCode parentName="p">{`text`}</inlineCode>{` `}<strong parentName="p">{`contains`}</strong>{` the input value. By default, we only show items that start with the input value.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const CustomSearchFilter = () => {
  const [filterVal, setFilterVal] = React.useState('')
  const handleChange = event => {
    setFilterVal(event.currentTarget.value)
  }
  const customFilterFn = item => item.text.includes(filterVal)

  return (
    <FormControl>
      <FormControl.Label id="autocompleteLabel-customFilter">Pick a branch</FormControl.Label>
      <Autocomplete>
        <Autocomplete.Input onChange={handleChange} />
        <Autocomplete.Overlay>
          <Autocomplete.Menu
            items={[
              {text: 'main', id: 0},
              {text: 'autocomplete-tests', id: 1},
              {text: 'a11y-improvements', id: 2},
              {text: 'button-bug-fixes', id: 3},
              {text: 'radio-input-component', id: 4},
              {text: 'release-1.0.0', id: 5},
              {text: 'text-input-implementation', id: 6},
              {text: 'visual-design-tweaks', id: 7},
            ]}
            selectedItemIds={[]}
            aria-labelledby="autocompleteLabel-customFilter"
            filterFn={customFilterFn}
          />
        </Autocomplete.Overlay>
      </Autocomplete>
    </FormControl>
  )
}

render(<CustomSearchFilter />)
`}</code></pre>
    <h4>{`Rendered without `}<inlineCode parentName="h4">{`Autocomplete.Overlay`}</inlineCode>{` with a `}<inlineCode parentName="h4">{`customScrollContainerRef`}</inlineCode></h4>
    <p>{`If a `}<inlineCode parentName="p">{`Autocomplete.Menu`}</inlineCode>{` is rendered without an `}<inlineCode parentName="p">{`Autocomplete.Overlay`}</inlineCode>{` inside of a scrollable container, the ref of the scrollable container must be passed to the `}<inlineCode parentName="p">{`customScrollContainerRef`}</inlineCode>{` to ensure that highlighted items are always scrolled into view.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const InOverlayWithCustomScrollContainerRef = () => {
  const scrollContainerRef = React.useRef(null)
  const inputRef = React.useRef(null)

  const [isOpen, setIsOpen] = React.useState(false)
  const handleOpen = () => {
    setIsOpen(true)
    inputRef.current && inputRef.current.focus()
  }

  return (
    <AnchoredOverlay
      open={isOpen}
      onOpen={handleOpen}
      onClose={() => setIsOpen(false)}
      width="large"
      height="xsmall"
      focusTrapSettings={{initialFocusRef: inputRef}}
      side="inside-top"
      renderAnchor={props => (
        <Button variant="invisible" {...props}>
          Pick branches
        </Button>
      )}
    >
      <FormControl>
        <FormControl.Label visuallyHidden id="autocompleteLabel-withCustomScrollRef">
          Pick branches
        </FormControl.Label>
        <Autocomplete>
          <Box display="flex" flexDirection="column" height="100%">
            <Box
              paddingX="3"
              paddingY="1"
              borderWidth={0}
              borderBottomWidth={1}
              borderColor="border.default"
              borderStyle="solid"
            >
              <Autocomplete.Input
                block
                as={TextInput}
                ref={inputRef}
                id="autocompleteInput"
                sx={{
                  display: 'flex',
                  border: '0',
                  padding: '0',
                  boxShadow: 'none',
                  ':focus-within': {
                    border: '0',
                    boxShadow: 'none',
                  },
                }}
              />
            </Box>
            <Box overflow="auto" flexGrow={1} ref={scrollContainerRef}>
              <Autocomplete.Menu
                items={[
                  {text: 'main', id: 0},
                  {text: 'autocomplete-tests', id: 1},
                  {text: 'a11y-improvements', id: 2},
                  {text: 'button-bug-fixes', id: 3},
                  {text: 'radio-input-component', id: 4},
                  {text: 'release-1.0.0', id: 5},
                  {text: 'text-input-implementation', id: 6},
                  {text: 'visual-design-tweaks', id: 7},
                ]}
                selectedItemIds={[]}
                customScrollContainerRef={scrollContainerRef}
                aria-labelledby="autocompleteLabel-withCustomScrollRef"
              />
            </Box>
          </Box>
        </Autocomplete>
      </FormControl>
    </AnchoredOverlay>
  )
}

render(<InOverlayWithCustomScrollContainerRef />)
`}</code></pre>
    <h4>{`Select multiple values`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const MultiSelect = () => {
  const items = [
    {text: 'main', id: 0},
    {text: 'autocomplete-tests', id: 1},
    {text: 'a11y-improvements', id: 22},
    {text: 'button-bug-fixes', id: 3},
    {text: 'radio-input-component', id: 4},
    {text: 'release-1.0.0', id: 5},
    {text: 'text-input-implementation', id: 6},
    {text: 'visual-design-tweaks', id: 7},
  ]
  const [selectedItemIds, setSelectedItemIds] = React.useState([])
  const onSelectedChange = newlySelectedItems => {
    if (!Array.isArray(newlySelectedItems)) {
      return
    }

    setSelectedItemIds(newlySelectedItems.map(item => item.id))
  }

  const getItemById = id => items.find(item => item.id === id)

  return (
    <Box display="flex" flexDirection="column" sx={{gap: '1em'}}>
      <FormControl>
        <FormControl.Label id="autocompleteLabel-multiselect">Pick branches</FormControl.Label>
        <Autocomplete>
          <Autocomplete.Input />
          <Autocomplete.Overlay>
            <Autocomplete.Menu
              items={items}
              selectedItemIds={selectedItemIds}
              aria-labelledby="autocompleteLabel-multiselect"
              onSelectedChange={onSelectedChange}
              selectionVariant="multiple"
            />
          </Autocomplete.Overlay>
        </Autocomplete>
      </FormControl>
      <div>
        <div>Selected items:</div>
        <Box as="ul" my={0}>
          {selectedItemIds.map(selectedItemId => (
            <li key={selectedItemId}>{getItemById(selectedItemId).text}</li>
          ))}
        </Box>
      </div>
    </Box>
  )
}

render(<MultiSelect />)
`}</code></pre>
    <h4>{`Select multiple values - new values can be added`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const MultiSelectAddNewItem = () => {
  const [selectedItemIds, setSelectedItemIds] = React.useState([])
  const onSelectedChange = newlySelectedItems => {
    if (!Array.isArray(newlySelectedItems)) {
      return
    }

    setSelectedItemIds(newlySelectedItems.map(item => item.id))
  }

  const [localItemsState, setLocalItemsState] = React.useState([
    {text: 'main', id: 0},
    {text: 'autocomplete-tests', id: 1},
    {text: 'a11y-improvements', id: 22},
    {text: 'button-bug-fixes', id: 3},
    {text: 'radio-input-component', id: 4},
    {text: 'release-1.0.0', id: 5},
    {text: 'text-input-implementation', id: 6},
    {text: 'visual-design-tweaks', id: 7},
  ])
  const getItemById = id => localItemsState.find(item => item.id === id)
  const [filterVal, setFilterVal] = React.useState('')

  const onItemSelect = item => {
    onSelectedChange([...selectedItemIds.map(id => localItemsState.find(selectedItem => selectedItem.id === id)), item])

    if (!localItemsState.some(localItem => localItem.id === item.id)) {
      setLocalItemsState([...localItemsState, item])
    }
  }

  const handleChange = event => {
    setFilterVal(event.currentTarget.value)
  }

  return (
    <Box display="flex" flexDirection="column" sx={{gap: '1em'}}>
      <FormControl>
        <FormControl.Label id="autocompleteLabel-addItem">Pick or add branches</FormControl.Label>
        <Autocomplete>
          <Autocomplete.Input onChange={handleChange} />
          <Autocomplete.Overlay>
            <Autocomplete.Menu
              addNewItem={
                filterVal && !localItemsState.map(localItem => localItem.text).includes(filterVal)
                  ? {
                      text: \`Add '\${filterVal}'\`,
                      handleAddItem: item => {
                        onItemSelect({
                          ...item,
                          text: filterVal,
                          selected: true,
                        })
                        setFilterVal('')
                      },
                    }
                  : undefined
              }
              items={localItemsState}
              selectedItemIds={selectedItemIds}
              onSelectedChange={onSelectedChange}
              selectionVariant="multiple"
              aria-labelledby="autocompleteLabel-addItem"
            />
          </Autocomplete.Overlay>
        </Autocomplete>
      </FormControl>
      <div>
        <div>Selected items:</div>
        <Box as="ul" my={0}>
          {selectedItemIds.map(selectedItemId => (
            <li key={selectedItemId}>{getItemById(selectedItemId).text}</li>
          ))}
        </Box>
      </div>
    </Box>
  )
}

render(<MultiSelectAddNewItem />)
`}</code></pre>
    <h4>{`Rendered with `}<inlineCode parentName="h4">{`Autocomplete.Context`}</inlineCode></h4>
    <p>{`The `}<inlineCode parentName="p">{`Autocomplete.Context`}</inlineCode>{` can be used to control the menu open/closed state and customize certain `}<inlineCode parentName="p">{`Autocomplete`}</inlineCode>{` behaviors`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`export function AutocompleteWithContext() {
  return (
    <Autocomplete>
      <AutocompleteWithContextInternal />
    </Autocomplete>
  )
}

export function AutocompleteWithContextInternal() {
  const autocompleteContext = useContext(Autocomplete.Context)
  if (autocompleteContext === null) {
    throw new Error('AutocompleteContext returned null values')
  }

  const {setShowMenu, showMenu} = autocompleteContext
  const inputRef = useRef < HTMLInputElement > null
  const [filterText, setFilterText] = useState('')

  useEffect(() => {
    if (!showMenu) {
      // keep the menu open
      setShowMenu(true)
    }
  }, [showMenu, setShowMenu])

  const change = event => setFilterText?.(event.target.value)

  return (
    <Autocomplete.Context.Provider
      value={{...autocompleteContext, autocompleteSuggestion: '', setAutocompleteSuggestion: () => false}}
    >
      <Autocomplete.Input ref={inputRef} value={filterText} onChange={change} />
      <Autocomplete.Overlay>
        <Autocomplete.Menu
          items={[
            {text: 'main', id: 0},
            {text: 'autocomplete-tests', id: 1},
            {text: 'a11y-improvements', id: 2},
            {text: 'button-bug-fixes', id: 3},
            {text: 'radio-input-component', id: 4},
            {text: 'release-1.0.0', id: 5},
            {text: 'text-input-implementation', id: 6},
            {text: 'visual-design-tweaks', id: 7},
          ]}
          selectedItemIds={[]}
          selectionVariant="single"
        />
      </Autocomplete.Overlay>
    </Autocomplete.Context.Provider>
  )
}
`}</code></pre>
    <h4>{`Rendered inside of a `}<inlineCode parentName="h4">{`Dialog`}</inlineCode></h4>
    <p>{`If you attempt to render an Autocomplete inside of a `}<a parentName="p" {...{
        "href": "/Dialog"
      }}>{`Dialog`}</a>{` (or any other component that overlays the page), the menu will be rendered below the parent overlay.`}</p>
    <p>{`There are two ways to get around this:`}</p>
    <ol>
      <li parentName="ol">{`Register a new `}<inlineCode parentName="li">{`Portal`}</inlineCode>{` root inside the `}<inlineCode parentName="li">{`Dialog`}</inlineCode>{`, then pass the name of that `}<inlineCode parentName="li">{`Portal`}</inlineCode>{` root to `}<inlineCode parentName="li">{`Autocomplete.Overlay`}</inlineCode>{`: `}<inlineCode parentName="li">{`<Autocomplete.Overlay portalContainerName="portal-inside-dialog">`}</inlineCode></li>
      <li parentName="ol">{`Wait to render the `}<inlineCode parentName="li">{`Autocomplete.Overlay`}</inlineCode>{` until after the `}<inlineCode parentName="li">{`Dialog`}</inlineCode>{` has opened`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`export function InADialog() {
  const outerContainerRef = React.useRef(null)
  const [mounted, setMounted] = React.useState(false)
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)

  useEffect(() => {
    if (outerContainerRef.current instanceof HTMLElement) {
      registerPortalRoot(outerContainerRef.current, 'outerContainer')
      setMounted(true)
    }
  }, [isDialogOpen])

  return (
    <>
      <Button onClick={() => setIsDialogOpen(!isDialogOpen)}>Show dialog</Button>
      <Dialog id="dialog-with-autocomplete" isOpen={isDialogOpen}>
        <div ref={outerContainerRef}>
          {mounted ? (
            <FormControl>
              <FormControl.Label id="autocompleteLabel" />
              <Autocomplete>
                <Autocomplete.Input data-testid="autocompleteInput" />
                <Autocomplete.Overlay portalContainerName="outerContainer">
                  <Autocomplete.Menu items={items} selectedItemIds={[]} aria-labelledby="autocompleteLabel" />
                </Autocomplete.Overlay>
              </Autocomplete>
            </FormControl>
          ) : null}
        </div>
      </Dialog>
      <p>
        The Autocomplete.Overlay is portalled to a div inside the Dialog to ensure it appears above the dialog in the
        stacking context.
      </p>
    </>
  )
}

render(<InADialog />)
`}</code></pre>
    <h2>{`Props`}</h2>
    <ComponentProps data={data} mdxType="ComponentProps" />
    <h2>{`Status`}</h2>
    <ComponentChecklist items={{
      propsDocumented: true,
      noUnnecessaryDeps: true,
      adaptsToThemes: true,
      adaptsToScreenSizes: true,
      fullTestCoverage: false,
      usedInProduction: false,
      usageExamplesDocumented: true,
      hasStorybookStories: true,
      designReviewed: false,
      a11yReviewed: false,
      stableApi: false,
      addressedApiFeedback: false,
      hasDesignGuidelines: true,
      hasFigmaComponent: true
    }} mdxType="ComponentChecklist" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      